<template>
  <SearchWrapper
    v-model:filtersData="selectedData"
    v-model:currentSort="selectedSorting"
    :isLoading="isLoading"
    :count="count"
    :filters="packagesStore.searchFilters"
    :hasTotalPrice="packagesStore.displayTotalPriceToggle()"
    :showSort="packageList.showSort"
  >
    <template v-slot:title>
      <span v-t="{ path: '{x} packages found', plural: count, args: { x: count } }" />
    </template>

    <template v-slot:results="{ seeTotalPrice }">
      <DbPackageResults
        :packages="sortedResults"
        :descriptionDisplay="packageList.shortDescriptionDisplay"
        :seeTotalPrice="seeTotalPrice"
        :seeSellingPoints="hotel.db2_key_selling_points"
        :nbrGridLayout="hotel.db2_package_layout"
        v-on:click-details="modalStore.showPackageModal($event)"
        v-on:click-select="selectPackage"
      />
    </template>

    <template
      v-if="
        availablePackages.length === 0 &&
        errorType !== 'MISSING_BOOKING_PARAM' &&
        packageList.noPackageImageUrl &&
        packageList.noPackageImageUrl !== '' &&
        showNoPackageImage
      "
      v-slot:no-results-image
    >
      <DbImage
        v-if="showNoPackageImage"
        class="db-no-package-image"
        :src="packageList.noPackageImageUrl"
        @error="handleNoPackageImageError"
      />
    </template>

    <template
      v-if="availablePackages.length === 0"
      v-slot:no-results-title
    >
      <div v-if="errorType === 'MISSING_BOOKING_PARAM'">
        {{ $t('Please enter your event date and expected number of attendees') }}
      </div>
      <div v-else>
        {{
          $t(
            'Unfortunately, there is no availability for the selected date and/or number of attendees',
          )
        }}
      </div>
    </template>

    <template
      v-if="availablePackages.length === 0"
      v-slot:no-results-message
    >
      <div v-if="errorType === 'MISSING_BOOKING_PARAM'" />
      <div v-else>
        <div>{{ $t('If possible, please search for alternative option or contact us') }}</div>

        <a
          v-if="funnel.requestAQuoteLink?.url"
          :href="funnel.requestAQuoteLink?.url"
          :target="funnel.requestAQuoteLink?.target ? '_blank' : undefined"
          class="db-button-tertiary db-button-large db-tw-mt-6 md:db-tw-mt-9"
        >
          {{ $t('Contact Hotel') }}
        </a>
      </div>
    </template>
  </SearchWrapper>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

import type { GalaxyPackage } from '@/types/galaxy-data/packages'
import { useGalaxyStaticSearch } from '@/composables/features/useGalaxyStaticSearch'
import type { SearchSort } from '@/features/SearchWrapper/SearchWrapperTypes'

import { useConfigurations } from '@/stores/configurations'
import { usePackagesStore } from '@/stores/packages'
import { useCartStore } from '@/stores/cart'
import { useFunnelRouter } from '@/AppBookingFunnel/composables/funnel-router'
import { storeToRefs } from 'pinia'
import { useModalsStore } from '@/stores/modals'
import type { GalaxyFunctionRoomSetups } from '@/types/galaxy-data/function-room-setups'
import type { GalaxyMeetingRoom } from '@/types/galaxy-data/meeting-room'
import { useI18n } from 'vue-i18n'

defineEmits<{
  (e: 'select-package', packageItem: GalaxyPackage): void
}>()

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    /** Additional filters not visible in the view, but to use when filtering */
    extraFilters?: Record<string, unknown>
  }>(),
  {
    extraFilters: () => ({}),
  },
)

const configStore = useConfigurations()
const { packageList, funnel } = storeToRefs(configStore)

const packagesStore = usePackagesStore()
const { apiLoading, availablePackages, errorType, date, attendees, packageID, meetingRoomID } =
  storeToRefs(packagesStore)

const cartStore = useCartStore()
const funnelRouter = useFunnelRouter()
const modalStore = useModalsStore()

import { useGtm } from '@/composables/features/useGtm'
const { event } = useGtm()

const hotel = window.db2BookingFunnel.getHotel()

const selectedData = ref({})
const allFilters = computed(() => {
  return {
    ...props.extraFilters,
    ...selectedData.value,
  }
})

/** Sorting `value` currently selected */
const selectedSorting = ref<SearchSort>('recommended')

const isLoading = computed(() => {
  return apiLoading.value || searchLoading.value
})

/** Init the search store */
const {
  isLoading: searchLoading,
  sortedResults,
  count,
} = useGalaxyStaticSearch({
  rawData: availablePackages,
  filters: allFilters,
  currentSort: selectedSorting,
})
const showNoPackageImage = ref(true)
const FunctionRoomSetups: GalaxyFunctionRoomSetups[] = packagesStore.getDb2FunctionRoomSetups()
let roomSetupOptions: { value: string; label: string }[] = []

if (configStore.packageList.showFilters) {
  watch(availablePackages, () => {
    refreshFilters()
  })
}

function refreshFilters() {
  if (availablePackages.value.length) {
    packagesStore.searchFilters.splice(0)
    // filter roomSetupOptions to exclude unused setup room
    if (configStore.packageList.showFilterFunctionRoomSetup) {
      let hasPackage = false
      roomSetupOptions = []
      FunctionRoomSetups.forEach((roomSetup) => {
        if (roomSetup.cle)
          roomSetupOptions.push({
            value: roomSetup.cle,
            label: roomSetup.name,
          })
      })
      roomSetupOptions.filter((value, index, arr) => {
        hasPackage = false
        availablePackages.value.forEach((item) => {
          if (item.functionRoomSetups.some((f) => f.cle === value.value)) {
            hasPackage = true
          }
        })
        if (!hasPackage) {
          arr.splice(index, 1)
        }
      })
      packagesStore.searchFilters.push({
        label: t('Function Room Setup'),
        key: 'functionRoomSetupsValues',
        options: roomSetupOptions,
      })
    }
    document.querySelector('.db-searchform-date')?.classList.remove('db-has-error')
    document.querySelector('.db-searchform-attendees')?.classList.remove('db-has-error')
  }
}

let watcherPackage = true
// Check if the venue is not already provide through the query url "packageID"
watch(
  [packageID],
  () => {
    // Case where the query is not define
    // (either undefined or string type: meaning waiting for api fetching)
    if (!watcherPackage || !packageID.value || typeof packageID.value === 'string') {
      return
    }
    selectPackage(packageID.value)
  },
  { immediate: true },
)

function selectPackage(param: GalaxyPackage) {
  if (!date.value) {
    document.querySelector('.db-searchform-date')?.classList.add('db-has-error')
    window.scrollTo(0, 0)
    return false
  }
  if (!attendees.value) {
    document.querySelector('.db-searchform-attendees')?.classList.add('db-has-error')
    window.scrollTo(0, 0)
    return false
  }
  cartStore.init(date.value, attendees.value, param.db2_packageid)
  watcherPackage = false //unload watcher
  // Set the packageID query URL (or override it depending of the situation)
  packageID.value = param
  // Unset the meetingRoomID query URL, if already set
  if (meetingRoomID.value && typeof meetingRoomID.value === 'object') {
    meetingRoomID.value = undefined
  }

  // When the "bestFit" mode is enable
  const packagesStore = usePackagesStore()
  const db2Packages = packagesStore.getDb2Package(param.db2_packageid).apiData
  if (!configStore.funnel.showVenueSelection) {
    if (
      db2Packages.meetingRooms.size < 1 ||
      db2Packages.meetingRooms.entries().next().value[0] === ''
    ) {
      throw new Error('areaSearch API: bestFit error')
    }
    const functRoomID = db2Packages.meetingRooms.entries().next().value[0]
    cartStore.setFunctionRoomBestFitMode(functRoomID, Array.from(db2Packages.meetingRooms.keys()))
    // Set the meetingRoomID query URL
    meetingRoomID.value = { db2_eventroomid: functRoomID } as GalaxyMeetingRoom
  }

  let attendees_bucket = ''
  if (packagesStore.attendees >= 0 && packagesStore.attendees <= 5) attendees_bucket = '0-5'
  if (packagesStore.attendees >= 6 && packagesStore.attendees <= 10) attendees_bucket = '6-10'
  if (packagesStore.attendees >= 11 && packagesStore.attendees <= 20) attendees_bucket = '11-20'
  if (packagesStore.attendees >= 21 && packagesStore.attendees <= 30) attendees_bucket = '21-30'
  if (packagesStore.attendees > 30) attendees_bucket = '+30'

  const availablePackageItemIdx = packagesStore.availablePackages.findIndex(
    (item) => item.db2_packageid === param.db2_packageid,
  )

  event('add_to_cart', {
    currency: db2Packages.currencyCode,
    items: {
      item_id: packageID.value.db2_packageid,
      item_name: packageID.value.name,
      affiliation: hotel.name,
      index: availablePackageItemIdx,
      item_brand: hotel.master_item?.name,
      item_category: 'meeting rooms packages',
      item_list_id: 'meeting_rooms_packages_list',
      item_list_name: 'Meeting Rooms Packages List',
      item_variant: '',
      attendees_bucket: attendees_bucket,
      quantity: 1,
    },
  })

  return !configStore.funnel.showVenueSelection
    ? funnelRouter.goToPackageConfiguration()
    : funnelRouter.goAfterPackageSelection()
}

const handleNoPackageImageError = () => {
  showNoPackageImage.value = false
}
</script>

<style scoped>
.db-app .db-no-package-image {
  margin-bottom: var(--db-spacer-8);
  width: 100px;
}

@media screen and (min-width: 992px) {
  .db-app .db-no-package-image {
    margin-bottom: var(--db-spacer-10);
    width: 150px;
  }
}
</style>
